import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Siren, Shield, MessageCircle, ThumbsUp, Plus, ChevronDown, BellRing, BellOff, Loader2, Send,
} from "lucide-react";
import { toast } from "sonner";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { enablePush, hasPushSubscription, pushSupported } from "@/lib/push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// Pillar 2 · 3-Minuten-Spickzettel
// ============================================================================
const SPICKZETTEL = [
  {
    head: "Ruhe ausstrahlen",
    body: "Die Prüfer kochen auch nur mit Wasser. Lächeln, Hände schütteln, in den separaten Raum führen. Kaffee anbieten. Die ersten 60 Sekunden bestimmen die Tonalität der gesamten Prüfung.",
  },
  {
    head: "Der Leitspruch",
    body: `„Alles, was nicht dokumentiert ist, ist nicht getan – aber alles, was dokumentiert ist, ist Gesetz." Diesen Satz im Kopf behalten und niemals improvisieren, was nicht im Bericht steht.`,
  },
  {
    head: "Das Recht auf Prüfungspause",
    body: `Die Prüfer dürfen nicht verlangen, dass du 8 Stunden am Stück ohne Pause Rede und Antwort stehst. Bestimme feste Zeiten: „Wir prüfen jetzt von 09:00 bis 12:00 Uhr die erste Stichprobe, dann machen wir 45 Minuten Mittagspause."`,
  },
];

// ============================================================================
// Pillar 3 · Konter-Matrix
// ============================================================================
const KONTER = [
  {
    title: "Falle 1 · Die Sturz-Prophylaxe",
    pruefer:
      `„Frau Müller ist letzte Woche gestürzt. Sie haben danach die Sturzrisiko-Einschätzung nicht aktualisiert. Das gibt Punktabzug!"`,
    konter:
      `„Das ist nicht korrekt. Schauen Sie bitte in den Pflegebericht vom Tag nach dem Sturz. Dort haben wir dokumentiert, dass die bestehenden Maßnahmen (Antirutschmatten, Rollatortraining) weiterhin passend und ausreichend sind. Eine formale Neuausstellung des Bogens ist laut dem aktuellen Strukturmodell bei unveränderter Risikolage nicht zwingend erforderlich."`,
  },
  {
    title: "Falle 2 · Das Flüssigkeits-Protokoll",
    pruefer:
      `„Herr Schmidt hat laut Protokoll gestern nur 900 ml getrunken. Das ist eine Verletzung der Sorgfaltspflicht!"`,
    konter:
      `„Wir dokumentieren personenzentriert. Aus dem Pflegebericht geht hervor, dass Herr Schmidt gestern aufgrund seiner fortgeschrittenen Demenz die Flüssigkeitsaufnahme aktiv und wiederholt verweigert hat. Wir haben ihm viermal wasserhaltige Nahrung (Suppe, Götterspeise) angeboten, was er angenommen hat. Die Versorgung war zu jedem Zeitpunkt gesichert, die Ablehnung wurde fachgerecht dokumentiert."`,
  },
  {
    title: "Falle 3 · Die Wunddokumentation",
    pruefer:
      `„Beim Dekubitus von Frau Weber fehlt das Foto vom letzten Verbandswechsel am Dienstag!"`,
    konter:
      `„Laut den nationalen Expertenstandards und unseren internen Verfahrensanweisungen ist eine Fotodokumentation alle 7 bis 14 Tage bei der Wundbeurteilung vorgesehen, um den Verlauf zu beurteilen. Der letzte Eintrag zeigt eine detaillierte schriftliche Beschreibung der Wundtiefe und des Wundgrundes durch unsere Wundexpertin. Ein tägliches oder lückenloses Foto ist rechtlich nicht gefordert."`,
  },
];

const TAG_COLORS = {
  "MDK-Prüfung": "bg-terra/12 text-terra",
  "Pflegesatz":  "bg-brand-rose/25 text-brand-red",
  "Recht":       "bg-powder/40 text-powder-dark",
  "Allgemein":   "bg-cream-200 text-sand-700",
};
const TAGS = ["MDK-Prüfung", "Pflegesatz", "Recht", "Allgemein"];

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return "gerade eben";
  if (sec < 3600) return `vor ${Math.floor(sec / 60)} Min`;
  if (sec < 86400) return `vor ${Math.floor(sec / 3600)} Std`;
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const inputCls =
  "rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14px] outline-none focus:border-terra";

// ============================================================================
// Push-Aktivierung
// ============================================================================
function PushCard() {
  const [state, setState] = useState("loading"); // loading | off | on | unsupported | busy
  useEffect(() => {
    if (!pushSupported()) { setState("unsupported"); return; }
    hasPushSubscription().then((on) => setState(on ? "on" : "off"));
  }, []);

  const activate = async () => {
    setState("busy");
    try {
      await enablePush();
      setState("on");
      toast.success("Push-Benachrichtigungen aktiviert – du wirst bei jedem Live-Alarm benachrichtigt.");
    } catch (e) {
      setState("off");
      toast.error(e?.message || "Push konnte nicht aktiviert werden.");
    }
  };

  if (state === "loading") return null;
  if (state === "unsupported") {
    return (
      <div className="mt-4 flex items-start gap-3 rounded-xl border border-cream-300 bg-cream-50 px-4 py-3" data-testid="push-unsupported">
        <BellOff className="h-4 w-4 text-sand-500 mt-0.5 shrink-0" strokeWidth={1.8} />
        <p className="text-[12.5px] text-sand-600 leading-relaxed">
          Dieser Browser unterstützt keine Push-Benachrichtigungen. Auf dem iPhone: Seite über
          „Teilen → Zum Home-Bildschirm" hinzufügen, dann dort öffnen. Du bekommst Alarme aber
          immer auch per E-Mail.
        </p>
      </div>
    );
  }
  if (state === "on") {
    return (
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-[#C9DDBE] bg-[#EEF4EA] px-4 py-3" data-testid="push-active">
        <BellRing className="h-4 w-4 text-[#3E6B2F] shrink-0" strokeWidth={2} />
        <p className="text-[12.5px] text-[#3E6B2F] font-medium">
          Push aktiv auf diesem Gerät – du wirst bei jedem Live-Alarm sofort benachrichtigt.
        </p>
      </div>
    );
  }
  return (
    <button
      onClick={activate}
      disabled={state === "busy"}
      data-testid="push-enable"
      className="mt-4 w-full flex items-center justify-between gap-3 rounded-xl border border-terra/25 bg-white px-4 py-3 text-left hover:bg-peach-50 transition-colors"
    >
      <span className="flex items-center gap-3">
        {state === "busy"
          ? <Loader2 className="h-4 w-4 text-terra animate-spin shrink-0" strokeWidth={2} />
          : <BellRing className="h-4 w-4 text-terra shrink-0" strokeWidth={2} />}
        <span className="text-[13px] text-sand-700">
          <strong className="font-semibold text-sand-800">Push-Benachrichtigungen aktivieren</strong> – damit
          dich der Rote Knopf auch erreicht, wenn du nicht im Campus bist.
        </span>
      </span>
      <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-terra shrink-0">Aktivieren</span>
    </button>
  );
}

// ============================================================================
// Thread-Eintrag mit Antworten & Like
// ============================================================================
function ThreadItem({ t, onChanged }) {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [liked, setLiked] = useState(t.liked_by_me);
  const [likeCount, setLikeCount] = useState(t.like_count);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && replies === null) {
      try {
        const { data } = await axios.get(`${API}/lagezentrum/threads/${t.id}/replies`);
        setReplies(data);
      } catch {
        setReplies([]);
      }
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/lagezentrum/threads/${t.id}/replies`, { body: draft.trim() });
      setReplies((r) => [...(r || []), data]);
      setDraft("");
      onChanged?.();
    } catch {
      toast.error("Antwort konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  const toggleLike = async () => {
    try {
      const { data } = await axios.post(`${API}/lagezentrum/threads/${t.id}/like`);
      setLiked(data.liked);
      setLikeCount(data.like_count);
    } catch {}
  };

  const repliesCount = replies ? replies.length : t.replies_count;

  return (
    <li data-testid={`thread-${t.id}`} className="py-5 -mx-3 px-3 rounded-xl transition-colors">
      <div className="flex items-start gap-5">
        <div className={`h-10 w-10 shrink-0 rounded-full grid place-items-center font-semibold text-[12.5px] ${t.alarm ? "bg-terra text-white" : "bg-terra/10 text-terra"}`}>
          {t.alarm ? <Siren className="h-4 w-4" strokeWidth={2} /> : (t.author_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-sand-500 mb-1.5">
            <span className="font-medium text-sand-700">{t.author_name}</span>
            <span>·</span>
            <span>{t.author_role}</span>
            <span>·</span>
            <span>{timeAgo(t.created_at)}</span>
            <span className={`ml-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10.5px] uppercase tracking-[0.15em] font-bold ${TAG_COLORS[t.tag] || "bg-cream-200 text-sand-700"}`}>
              {t.tag}
            </span>
            {t.alarm && (
              <span className="inline-flex items-center gap-1 text-[11px] text-terra font-semibold uppercase tracking-[0.15em]">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-terra animate-pulse" />
                Live-Alarm
              </span>
            )}
          </div>
          <button onClick={toggleOpen} className="text-left w-full" data-testid={`thread-open-${t.id}`}>
            <h4 className={`font-display text-sand-800 text-[1.05rem] font-semibold leading-snug ${t.alarm ? "text-terra" : ""}`}>
              {t.title}
            </h4>
            {t.body && (
              <p className={`mt-1.5 text-[13.5px] text-sand-600 leading-relaxed ${open ? "" : "line-clamp-2"}`}>
                {t.body}
              </p>
            )}
          </button>
          <div className="mt-3 flex items-center gap-5 text-[12px] text-sand-500">
            <button onClick={toggleOpen} className="inline-flex items-center gap-1.5 hover:text-terra" data-testid={`thread-replies-${t.id}`}>
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.6} />
              {repliesCount} {repliesCount === 1 ? "Antwort" : "Antworten"}
            </button>
            <button onClick={toggleLike} data-testid={`thread-like-${t.id}`}
              className={`inline-flex items-center gap-1.5 ${liked ? "text-terra font-semibold" : "hover:text-terra"}`}>
              <ThumbsUp className="h-3.5 w-3.5" strokeWidth={liked ? 2.2 : 1.6} />
              {likeCount}
            </button>
          </div>

          {open && (
            <div className="mt-4 space-y-4" data-testid={`thread-detail-${t.id}`}>
              {replies === null ? (
                <div className="flex items-center gap-2 text-[13px] text-sand-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Antworten werden geladen …
                </div>
              ) : (
                replies.map((r) => (
                  <div key={r.id} className="rounded-xl bg-cream-100 border border-cream-300 px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-sand-500 mb-1">
                      <span className="font-semibold text-sand-700">{r.author_name}</span>
                      <span>·</span><span>{r.author_role}</span>
                      <span>·</span><span>{timeAgo(r.created_at)}</span>
                    </div>
                    <p className="text-[13.5px] text-sand-700 leading-relaxed whitespace-pre-wrap">{r.body}</p>
                  </div>
                ))
              )}
              <form onSubmit={sendReply} className="flex items-start gap-2">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Deine Antwort …"
                  rows={2}
                  data-testid={`reply-input-${t.id}`}
                  className="flex-1 rounded-xl border border-cream-300 bg-white px-4 py-2.5 text-[14px] outline-none focus:border-terra resize-y"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  data-testid={`reply-submit-${t.id}`}
                  className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark disabled:opacity-50 text-white h-10 px-4 text-[13px] font-medium mt-0.5"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-3.5 w-3.5" strokeWidth={1.8} />}
                  Antworten
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// ============================================================================
// Page component
// ============================================================================
export default function CampusLagezentrum() {
  const { user } = useCampusAuth();
  const [threads, setThreads] = useState(null);
  const [alarmOpen, setAlarmOpen] = useState(false);
  const [alarmBusy, setAlarmBusy] = useState(false);
  const [alarmDraft, setAlarmDraft] = useState({
    einrichtung: user?.organisation || "",
    bundesland: "NRW",
    schwerpunkt: "",
  });
  const [composerOpen, setComposerOpen] = useState(false);
  const [composer, setComposer] = useState({ title: "", body: "", tag: "MDK-Prüfung" });
  const [composerBusy, setComposerBusy] = useState(false);
  const [openKonter, setOpenKonter] = useState(0);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lagezentrum/threads`);
      setThreads(data);
    } catch {
      setThreads([]);
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const triggerAlarm = async (e) => {
    e.preventDefault();
    if (!alarmDraft.einrichtung.trim()) return;
    setAlarmBusy(true);
    try {
      await axios.post(`${API}/lagezentrum/alarm`, {
        einrichtung: alarmDraft.einrichtung.trim(),
        bundesland: alarmDraft.bundesland.trim(),
        schwerpunkt: alarmDraft.schwerpunkt.trim() || null,
      });
      toast.success("Live-Alarm ausgelöst – alle Club-Mitglieder werden per Push & E-Mail benachrichtigt.");
      setAlarmDraft({ ...alarmDraft, schwerpunkt: "" });
      setAlarmOpen(false);
      loadThreads();
    } catch {
      toast.error("Alarm konnte nicht ausgelöst werden – bitte erneut versuchen.");
    } finally {
      setAlarmBusy(false);
    }
  };

  const submitThread = async (e) => {
    e.preventDefault();
    if (!composer.title.trim()) return;
    setComposerBusy(true);
    try {
      await axios.post(`${API}/lagezentrum/threads`, {
        title: composer.title.trim(),
        body: composer.body.trim() || null,
        tag: composer.tag,
      });
      setComposer({ title: "", body: "", tag: "MDK-Prüfung" });
      setComposerOpen(false);
      loadThreads();
    } catch {
      toast.error("Thread konnte nicht erstellt werden.");
    } finally {
      setComposerBusy(false);
    }
  };

  return (
    <div className="space-y-14" data-testid="lagezentrum-page">
      {/* Header */}
      <header>
        <div className="text-[12px] uppercase tracking-[0.28em] font-medium text-sand-500 mb-3">
          MDK-Schutzschild  ·  3 Säulen
        </div>
        <h1 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold leading-[1.1]">
          #mdk-lagezentrum
        </h1>
        <p className="mt-4 text-sand-600 text-[15.5px] leading-[1.65] max-w-2xl">
          Dein digitaler Airbag für die Qualitätsprüfung. Live-Alarm, Sofort-Spickzettel
          und die Konter-Matrix für das Abschlussgespräch.
        </p>
      </header>

      {/* ====================================================================
          SÄULE 1 · DER ROTE KNOPF + COMMUNITY-FEED
      ====================================================================== */}
      <section data-testid="saeule-1">
        <div className="flex items-baseline gap-4 mb-6">
          <div className="font-display text-[2rem] text-terra leading-none tabular-nums font-semibold">01</div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sand-500">
              Säule 1  ·  Live-Alarm
            </div>
            <h2 className="font-display text-sand-800 text-[1.5rem] font-semibold leading-tight mt-1">
              Der Rote Knopf.
            </h2>
          </div>
        </div>

        {/* The actual red button – the visual focal point of the entire page */}
        <div className="rounded-2xl bg-terra/5 border border-terra/20 p-6 sm:p-8">
          {!alarmOpen ? (
            <button
              onClick={() => setAlarmOpen(true)}
              data-testid="alarm-open"
              className="w-full grid sm:grid-cols-[auto_1fr_auto] items-center gap-5 text-left"
            >
              <span className="inline-flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-full bg-terra text-white shadow-[0_8px_20px_-8px_rgba(190,53,25,0.65)]">
                <Siren className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.8} />
              </span>
              <div>
                <div className="font-display text-sand-800 text-[1.25rem] sm:text-[1.4rem] font-semibold leading-tight">
                  Live-Alarm aktivieren
                </div>
                <p className="mt-1.5 text-[13.5px] text-sand-600 leading-relaxed">
                  Benachrichtigt alle Club-Mitglieder per Push &amp; E-Mail.
                  Du bist in unter 5 Minuten nicht mehr allein.
                </p>
              </div>
              <span className="hidden sm:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] font-bold text-terra">
                <BellRing className="h-3.5 w-3.5" strokeWidth={2} />
                Push + E-Mail
              </span>
            </button>
          ) : (
            <form onSubmit={triggerAlarm} className="grid gap-4" data-testid="alarm-form">
              <div className="flex items-center gap-3">
                <Siren className="h-5 w-5 text-terra" strokeWidth={2} />
                <h3 className="font-display text-[1.15rem] font-semibold text-sand-800">
                  Alarm-Details
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  required
                  value={alarmDraft.einrichtung}
                  onChange={(e) => setAlarmDraft({ ...alarmDraft, einrichtung: e.target.value })}
                  placeholder="Einrichtung"
                  data-testid="alarm-einrichtung"
                  className={inputCls}
                />
                <input
                  required
                  value={alarmDraft.bundesland}
                  onChange={(e) => setAlarmDraft({ ...alarmDraft, bundesland: e.target.value })}
                  placeholder="Bundesland"
                  data-testid="alarm-bundesland"
                  className={inputCls}
                />
              </div>
              <input
                value={alarmDraft.schwerpunkt}
                onChange={(e) => setAlarmDraft({ ...alarmDraft, schwerpunkt: e.target.value })}
                placeholder="Schwerpunkt der Prüfung (optional, z. B. Wundmanagement)"
                data-testid="alarm-schwerpunkt"
                className={inputCls}
              />
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setAlarmOpen(false)}
                  className="text-[13px] text-sand-600 hover:text-sand-800"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={alarmBusy}
                  data-testid="alarm-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark disabled:opacity-60 text-white h-11 px-5 text-[13.5px] font-medium"
                >
                  {alarmBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Siren className="h-4 w-4" strokeWidth={1.8} />}
                  Alarm jetzt auslösen
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Push opt-in */}
        <PushCard />

        {/* Community thread feed */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <h3 className="font-display text-sand-800 text-[1.1rem] font-semibold">
              Lagezentrum-Feed
            </h3>
            <div className="flex items-center gap-4">
              {threads && (
                <span className="text-[12px] text-sand-500">
                  {threads.length} Threads · {threads.filter((t) => t.alarm).length} Alarme
                </span>
              )}
              <button
                onClick={() => setComposerOpen((v) => !v)}
                data-testid="composer-toggle"
                className="inline-flex items-center gap-1.5 rounded-full border border-terra/30 text-terra hover:bg-peach-50 h-9 px-4 text-[12.5px] font-semibold"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
                Frage stellen
              </button>
            </div>
          </div>

          {composerOpen && (
            <form onSubmit={submitThread} className="mb-6 grid gap-3 rounded-2xl border border-cream-300 bg-white p-5" data-testid="composer-form">
              <input
                required
                value={composer.title}
                onChange={(e) => setComposer({ ...composer, title: e.target.value })}
                placeholder="Worum geht es? (Titel)"
                data-testid="composer-title"
                className={inputCls}
              />
              <textarea
                value={composer.body}
                onChange={(e) => setComposer({ ...composer, body: e.target.value })}
                placeholder="Beschreibe deine Situation oder Frage … (optional)"
                rows={3}
                data-testid="composer-body"
                className="rounded-xl border border-cream-300 bg-white px-4 py-2.5 text-[14px] outline-none focus:border-terra resize-y"
              />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  {TAGS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setComposer({ ...composer, tag })}
                      data-testid={`composer-tag-${tag}`}
                      className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.12em] font-bold border transition-colors ${
                        composer.tag === tag
                          ? "border-terra bg-terra text-white"
                          : "border-cream-300 bg-cream-50 text-sand-600 hover:border-terra/40"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={composerBusy || !composer.title.trim()}
                  data-testid="composer-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark disabled:opacity-50 text-white h-10 px-5 text-[13px] font-medium"
                >
                  {composerBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-3.5 w-3.5" strokeWidth={1.8} />}
                  Veröffentlichen
                </button>
              </div>
            </form>
          )}

          {threads === null ? (
            <div className="py-12 flex items-center justify-center gap-2 text-sand-500 text-[14px]">
              <Loader2 className="h-4 w-4 animate-spin" /> Feed wird geladen …
            </div>
          ) : threads.length === 0 ? (
            <div className="py-12 text-center border-y border-cream-300" data-testid="feed-empty">
              <MessageCircle className="h-6 w-6 text-sand-400 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[14px] text-sand-600">
                Noch keine Threads. Stelle die erste Frage – oder drücke im Ernstfall den Roten Knopf.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-cream-300 border-y border-cream-300">
              {threads.map((t) => (
                <ThreadItem key={t.id} t={t} onChanged={loadThreads} />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ====================================================================
          SÄULE 2 · 3-MINUTEN-SPICKZETTEL
      ====================================================================== */}
      <section data-testid="saeule-2">
        <div className="flex items-baseline gap-4 mb-6">
          <div className="font-display text-[2rem] text-terra leading-none tabular-nums font-semibold">02</div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sand-500">
              Säule 2  ·  3-Minuten-Spickzettel
            </div>
            <h2 className="font-display text-sand-800 text-[1.5rem] font-semibold leading-tight mt-1">
              MDK-Knigge für den Empfang.
            </h2>
          </div>
        </div>
        <p className="text-sand-600 text-[14.5px] leading-[1.7] max-w-2xl mb-6">
          Öffne diesen Text auf dem Smartphone, während du zur Tür gehst, um die
          Prüfer:innen zu begrüßen. Die ersten 3 Minuten bestimmen die Tonalität
          der gesamten Prüfung.
        </p>
        <ol className="divide-y divide-cream-300 border-y border-cream-300">
          {SPICKZETTEL.map((s, i) => (
            <li key={s.head} className="py-6 grid grid-cols-[44px_1fr] gap-x-5">
              <div className="font-display text-[1.5rem] text-sand-400 leading-none tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div>
                <h3 className="font-display text-sand-800 text-[1.1rem] font-semibold leading-tight">
                  {s.head}
                </h3>
                <p className="mt-2 text-sand-600 text-[14px] leading-[1.7]">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ====================================================================
          SÄULE 3 · KONTER-MATRIX
      ====================================================================== */}
      <section data-testid="saeule-3">
        <div className="flex items-baseline gap-4 mb-6">
          <div className="font-display text-[2rem] text-terra leading-none tabular-nums font-semibold">03</div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-sand-500">
              Säule 3  ·  Konter-Matrix
            </div>
            <h2 className="font-display text-sand-800 text-[1.5rem] font-semibold leading-tight mt-1">
              Harte Argumente bei unberechtigter Kritik.
            </h2>
          </div>
        </div>
        <p className="text-sand-600 text-[14.5px] leading-[1.7] max-w-2xl mb-6">
          Drei häufige Fallen – mit dem exakten Konter-Text für das Abschluss­gespräch.
          Klick auf die Falle, um die Antwort zu sehen.
        </p>
        <div className="divide-y divide-cream-300 border-y border-cream-300">
          {KONTER.map((k, i) => {
            const open = openKonter === i;
            return (
              <div key={k.title} data-testid={`konter-${i + 1}`}>
                <button
                  onClick={() => setOpenKonter(open ? -1 : i)}
                  className="w-full py-6 grid grid-cols-[1fr_auto] items-start gap-4 text-left"
                >
                  <div>
                    <div className="font-display text-sand-800 text-[1.1rem] font-semibold leading-tight">
                      {k.title}
                    </div>
                    {!open && (
                      <p className="mt-2 text-[13.5px] text-sand-600 italic leading-snug line-clamp-1">
                        Prüfer sagt: {k.pruefer}
                      </p>
                    )}
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-sand-500 transition-transform ${open ? "rotate-180" : ""}`}
                    strokeWidth={1.6}
                  />
                </button>
                {open && (
                  <div className="pb-7 grid gap-4">
                    <div className="rounded-xl bg-cream-100 border border-cream-300 p-5">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-sand-500 mb-2">
                        Prüfer:in sagt
                      </div>
                      <p className="text-sand-700 text-[14px] leading-[1.65] italic">
                        {k.pruefer}
                      </p>
                    </div>
                    <div className="rounded-xl bg-peach-50 border border-terra/20 p-5">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-terra mb-2 inline-flex items-center gap-2">
                        <Shield className="h-3 w-3" strokeWidth={2.2} />
                        Helden-Konter
                      </div>
                      <p className="text-sand-800 text-[14px] leading-[1.65]">
                        {k.konter}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
